import type { AnonymousUserInfoModel, GlobalPermissionModel, SessionInfoModel, SourcePermissionModel, UserInfoModel, UserSettingsInfoModel } from "../../../OpenApi/api";
import { UserIdentifier } from "../../app/viewer/domain/UserIdentifier";
import { IFetcher } from "../../util/io/Fetcher";

export interface ISessionStore {
	readonly isLoggedIn: boolean;
	readonly isUserOrAnonymousImpersonatedUser: boolean;
	readonly isAnonymousUser: boolean;
	readonly currentUser: UserInfoModel | null;
	readonly currentAnonymousUser: AnonymousUserInfoModel | null;
	readonly currentUserIsGlobalAdmin: boolean;
	readonly currentUserIsUserOrImpersonatedUser: boolean;
	readonly currentUserIsImpersonatedUser: boolean;
	readonly currentUserOrImpersonatedUserIdentifier: UserIdentifier | null;

	/**
	 * Contains the current user settings of the current user.
	 * If the current user is impersonated (EMR), it'll contain the user settings of the original user
	 * Prefer to use this instead of `currentUser.settings`, because it will also contain the settings of the impersonated user.
	 */
	readonly currentUserSettings: UserSettingsInfoModel | null;

	currentUserIsSourceAdmin(sourceId: number): boolean;
	currentUserIsSourceUser(sourceId: number): boolean;
	currentUserHasGlobalPermission(permission: GlobalPermissionModel): boolean;
	currentUserHasSourcePermission(sourceId: number, permission: SourcePermissionModel): boolean;
	currentUserHasDepartmentPermission(sourceId: number, departmentId: number | null | undefined, permission: SourcePermissionModel): boolean;
	currentUserHasPermissionInAnySource(permission: SourcePermissionModel): boolean;

	logout(): Promise<void>;
}

export class SessionStore implements ISessionStore {
	constructor(
		private readonly sessionInfo: SessionInfoModel | null,
		private readonly fetcher: IFetcher,
	) {}

	get isLoggedIn(): boolean {
		return this.currentUser != null || this.currentAnonymousUser != null;
	}

	get isUserOrAnonymousImpersonatedUser(): boolean {
		return !!this.currentUser || !!this.currentAnonymousUser?.anonymousUserWithEmrToken?.impersonatedUserId;
	}

	get isAnonymousUser(): boolean {
		return !!this.currentAnonymousUser;
	}

	get currentUser(): UserInfoModel | null {
		return this.sessionInfo?.currentUser ?? null;
	}

	get currentAnonymousUser(): AnonymousUserInfoModel | null {
		return this.sessionInfo?.currentAnonymousUser ?? null;
	}

	get currentUserIsGlobalAdmin(): boolean {
		return this.currentUser?.isGlobalAdmin ?? false;
	}

	get currentUserIsUserOrImpersonatedUser(): boolean {
		if (this.currentUser) {
			return true;
		}

		if (this.currentAnonymousUser?.anonymousUserWithEmrToken?.impersonatedUserId) {
			return true;
		}

		return false;
	}

	get currentUserIsImpersonatedUser(): boolean {
		if (this.currentAnonymousUser?.anonymousUserWithEmrToken?.impersonatedUserId) {
			return true;
		}
		return false;
	}

	get currentUserSettings(): UserSettingsInfoModel | null {
		return this.currentUser?.settings ?? this.currentAnonymousUser?.anonymousUserWithEmrToken?.settings ?? null;
	}

	get currentUserOrImpersonatedUserIdentifier(): UserIdentifier | null {
		const { currentUser, currentAnonymousUser } = this;
		if (currentUser) {
			return UserIdentifier.byPrimaryKey(currentUser.id);
		}
		if (currentAnonymousUser?.anonymousUserWithEmrToken?.impersonatedUserId) {
			return UserIdentifier.byPrimaryKey(currentAnonymousUser.anonymousUserWithEmrToken.impersonatedUserId);
		}
		return null;
	}

	currentUserHasGlobalPermission(permission: GlobalPermissionModel): boolean {
		return this.currentUserIsGlobalAdmin || this.currentUser?.globalPermissions.includes(permission) === true;
	}

	currentUserHasSourcePermission(sourceId: number, permission: SourcePermissionModel): boolean {
		return this.currentUser?.sourcePermissions[sourceId]?.map(sp => sp.permission).includes(permission) === true;
	}

	currentUserHasDepartmentPermission(sourceId: number, departmentId: number | null | undefined, permission: SourcePermissionModel): boolean {
		const sourcePermissions = this.currentUser?.sourcePermissions[sourceId];
		if (departmentId === null || departmentId === undefined) {
			return sourcePermissions?.some(sp => sp.permission === permission && sp.limitedToDepartments === null) === true;
		}
		return sourcePermissions?.some(sp => sp.permission === permission && (sp.limitedToDepartments?.includes(departmentId) || sp.limitedToDepartments === null)) === true;
	}

	currentUserHasPermissionInAnySource(permission: SourcePermissionModel): boolean {
		const { currentUser } = this;
		if (!currentUser) return false;

		const sourceIds = Object.keys(currentUser.sourcePermissions);
		return sourceIds.some(sourceId => currentUser.sourcePermissions[sourceId].map(sp => sp.permission).includes(permission));
	}

	currentUserIsSourceAdmin(sourceId: number): boolean {
		const { currentUser } = this;
		if (!currentUser) return false;
		const { sources } = currentUser;
		const { sourceIdsWhereUserIsSourceAdmin } = sources;
		return sourceIdsWhereUserIsSourceAdmin.includes(sourceId);
	}

	currentUserIsSourceUser(sourceId: number): boolean {
		const { currentUser } = this;
		if (!currentUser) return false;
		const { sources } = currentUser;
		const { sourceIdsWhereUserIsSourceUser } = sources;
		return sourceIdsWhereUserIsSourceUser.includes(sourceId);
	}

	async logout(): Promise<void> {
		const { fetcher } = this;
		await fetcher.get("/POW4/authentication/logout", { redirect: "manual" });
	}
}
